import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import StatCard from '../../components/common/StatCard';
import DonutChart from '../../components/common/DonutChart';
import Avatar from '../../components/common/Avatar';
import Spinner from '../../components/common/Spinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import DonationForm from './DonationForm';
import ExpenseForm from './ExpenseForm';
import useFundraisingStore, { fundTotals } from '../../store/useFundraisingStore';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import useMyProfileStore from '../../store/useMyProfileStore';
import fundraisingService, { findParentById } from '../../api/services/fundraising.service';
import roomParentsService from '../../api/services/roomParents.service';
import useGmailStore, { getGmailAccessToken } from '../../store/useGmailStore';
import { sendGmailMessage } from '../../api/gmail';
import { buildDonationEmail } from '../../emails/donationConfirmation';
import { isValidEmail } from '../../utils/email';
import { showToast } from '../../store/useToastStore';
import { fmt, fmtDate } from '../../utils/formatters';
import { toChartEntries } from '../../utils/charts';
import { donationSourceLabel } from '../../utils/constants';
import { avatarFor, initialsOf } from '../../utils/avatar';
import { IconExport, IconFile, IconTrash } from '../../components/icons';

export default function FundraisingPage() {
  const { donations, donationsLoading, loadDonations, expenses, expensesLoading, loadExpenses } = useFundraisingStore();
  const ctx = useContextStore(selectCurrentContext);
  const me = useMyProfileStore((s) => s.me);
  const location = useLocation();

  const gmailAccount = useGmailStore((s) => s.account);

  const myUserId = me?.id || me?.userId || me?.user_id || '';
  const myName = me?.user_metadata?.name || '';

  const networkId = ctx?.schoolId ? ctx.id : null;

  const [donFormOpen, setDonFormOpen] = useState(() => !!location.state?.openDonationForm);
  const [donOpenCount, setDonOpenCount] = useState(() => (location.state?.openDonationForm ? 1 : 0));
  const [editingDonation, setEditingDonation] = useState(null);
  const [expFormOpen, setExpFormOpen] = useState(false);
  const [expOpenCount, setExpOpenCount] = useState(0);
  const [editingExpense, setEditingExpense] = useState(null);

  /* The class roster — the same list the Parents page shows, app members and
     email-only contacts alike. It feeds the donor dropdown, the donation row
     avatars and the "Logged by" line on both ledgers. Rows carry a userId
     (app account) and/or a parentId (roster record); see findParentById. */
  const [classParents, setClassParents] = useState([]);

  useEffect(() => {
    if (!networkId) return undefined;
    loadDonations(networkId, { force: true }).catch((err) => showToast(err.message));
    loadExpenses(networkId, { force: true }).catch((err) => showToast(err.message));
    let cancelled = false;
    roomParentsService
      .getAllParents(networkId)
      .then((parents) => { if (!cancelled) setClassParents(parents); })
      .catch(() => { if (!cancelled) setClassParents([]); });
    return () => { cancelled = true; };
  }, [networkId, loadDonations, loadExpenses]);

  const memberById = (uid) => findParentById(classParents, uid);
  const donorName = (d) => d.parentName || memberById(d.userId)?.name || 'Parent';

  /* "Logged by" — the API returns the recorder's id only, so resolve it against
     the class roster, falling back to my own name when the row is mine. Both
     ledgers are currently scoped server-side to the signed-in room parent, so
     in practice that is every row; this reads correctly either way once the
     API starts returning the whole class's entries. */
  const loggedByName = (row) => {
    const uid = row.loggedById;
    if (!uid) return '';
    if (uid === myUserId) return myName;
    return memberById(uid)?.name || '';
  };

  const { raised, spent, balance } = fundTotals(donations, expenses);

  /* API stores the donation reason as a plain label string — group on it directly. */
  const donationsByReason = {};
  donations.forEach((d) => {
    const label = d.reason || 'Other';
    donationsByReason[label] = (donationsByReason[label] || 0) + Number(d.amount);
  });
  const expensesByTag = {};
  expenses.forEach((e) => {
    const label = e.category || 'Other';
    expensesByTag[label] = (expensesByTag[label] || 0) + Number(e.amount);
  });

  const openDonForm = (donation = null) => {
    setEditingDonation(donation);
    setDonFormOpen(true);
    setDonOpenCount((c) => c + 1);
  };

  const closeDonForm = () => {
    setDonFormOpen(false);
    setEditingDonation(null);
  };

  /* The confirmation email for a donation as the form holds it (spec 23). The
     form's Preview and the send after Save both use this, so they're the same
     message. */
  const confirmationEmailFor = (payload) => {
    const parent = findParentById(classParents, payload.userId);
    return {
      to: (parent?.email || '').trim(),
      /* opts.forSend: the logo goes inside the message instead of by URL */
      build: (opts) =>
        buildDonationEmail(
          {
            donation: payload,
            parentName: parent?.name || '',
            className: ctx?.name || '',
            schoolName: ctx?.schoolName || '',
            roomParentName: myName || gmailAccount?.name || '',
          },
          opts
        ),
    };
  };

  /* A new donation emails the parent once, from the room parent's connected
     Gmail (like class emails) — never on edit or delete. Best-effort: the
     donation is already saved, so this only decides what the toast says. */
  const sendConfirmation = async (payload) => {
    const email = confirmationEmailFor(payload);
    if (!isValidEmail(email.to)) return 'Donation logged ✓ — no email on file, so no confirmation was sent.';
    if (!gmailAccount) return 'Donation logged ✓ — connect Gmail at the top right to email parents a confirmation.';
    try {
      const { subject, html, inlineImages } = await email.build({ forSend: true });
      await sendGmailMessage({
        to: [email.to],
        subject,
        html,
        inlineImages,
        fromName: `${ctx?.name || 'Your class'} via RoomParent`,
        fromEmail: gmailAccount.email,
      });
      return `Donation logged ✓ — confirmation emailed to ${email.to}`;
    } catch (err) {
      return `Donation logged ✓, but the confirmation email didn't send: ${err.message}`;
    }
  };

  /* Saving a new donation with Gmail disconnected would log it silently with no
     email — so ask first. Resolves true for "Save without email", false for
     Cancel (the form keeps what was typed, to connect and save again). Only
     asked when an email would otherwise have gone out. */
  const connectGmail = useGmailStore((s) => s.connect);
  const [noGmailAsk, setNoGmailAsk] = useState(null); // { parentName, resolve }
  const askSaveWithoutEmail = (parentName) => new Promise((resolve) => setNoGmailAsk({ parentName, resolve }));
  const answerNoGmail = (saveAnyway) => {
    noGmailAsk?.resolve(saveAnyway);
    setNoGmailAsk(null);
  };

  /* POST for new donations; PUT with only the changed keys for edits. */
  const saveDonation = async (payload) => {
    let savingWithoutEmail = false;
    /* set when Gmail couldn't be reached at Save — the donation still saves,
       but no second Google window opens for the email */
    let noEmailReason = '';
    const parent = editingDonation ? null : findParentById(classParents, payload.userId);
    if (parent && isValidEmail(parent.email || '')) {
      if (!gmailAccount) {
        if (!(await askSaveWithoutEmail(parent.name || ''))) return false;
        savingWithoutEmail = true;
      } else {
        /* Renew the Gmail token now, while this still counts as the Save click:
           Google's window, opened later (after the donation request), can be
           blocked as a popup. Nothing happens while the token is fresh. */
        try {
          await getGmailAccessToken();
        } catch (err) {
          noEmailReason =
            err.gsiType === 'popup_closed'
              ? "Google's window was closed, so no confirmation email was sent."
              : `the confirmation email didn't send: ${err.message}`;
        }
      }
    }
    try {
      if (editingDonation) {
        const d = editingDonation;
        const patch = { id: d.id };
        if (payload.userId !== d.userId) patch.userId = payload.userId;
        if (payload.amount !== d.amount) patch.amount = payload.amount;
        if (payload.source !== d.source) patch.source = payload.source;
        if (payload.dateReceived !== d.date) patch.dateReceived = payload.dateReceived;
        if (payload.reason !== d.reason) patch.reason = payload.reason;
        if (payload.note !== d.note) patch.note = payload.note;
        if (Object.keys(patch).length > 1) await fundraisingService.updateDonation(patch);
        showToast('Donation updated ✓');
      } else {
        await fundraisingService.addDonation({ networkId, ...payload });
        showToast(
          savingWithoutEmail
            ? 'Donation logged ✓ — saved without a confirmation email.'
            : noEmailReason
              ? `Donation logged ✓ — ${noEmailReason}`
              : await sendConfirmation(payload)
        );
      }
      closeDonForm();
      await loadDonations(networkId, { force: true });
      return true;
    } catch (err) {
      showToast(err.message);
      return false;
    }
  };

  const openExpForm = (expense = null) => {
    setEditingExpense(expense);
    setExpFormOpen(true);
    setExpOpenCount((c) => c + 1);
  };

  const closeExpForm = () => {
    setExpFormOpen(false);
    setEditingExpense(null);
  };

  /* POST for new expenses; PUT with only the changed fields (multipart — a new
     receipt file replaces the stored URL). */
  const saveExpense = async ({ receiptFile, ...payload }) => {
    try {
      if (editingExpense) {
        const ex = editingExpense;
        const patch = { id: ex.id };
        if (payload.category !== ex.category) patch.category = payload.category;
        if (payload.amount !== ex.amount) patch.amount = payload.amount;
        if (payload.dateSpent !== ex.date) patch.dateSpent = payload.dateSpent;
        if (payload.usedFor !== ex.desc) patch.usedFor = payload.usedFor;
        if (Object.keys(patch).length > 1 || receiptFile) await fundraisingService.updateExpense(patch, receiptFile);
        showToast('Expense updated ✓');
      } else {
        await fundraisingService.addExpense({ networkId, ...payload }, receiptFile);
        showToast('Expense logged ✓');
      }
      closeExpForm();
      await loadExpenses(networkId, { force: true });
      return true;
    } catch (err) {
      showToast(err.message);
      return false;
    }
  };

  const deactivateExpense = async (e) => {
    if (!window.confirm(`Remove the ${fmt(e.amount)} expense "${e.desc}"?\n\nIt will no longer count toward totals.`)) return;
    try {
      await fundraisingService.updateExpense({ id: e.id, status: 'inactive' });
      showToast('Expense removed ✓');
      await loadExpenses(networkId, { force: true });
    } catch (err) {
      showToast(err.message);
    }
  };

  const deactivateDonation = async (d) => {
    if (!window.confirm(`Remove the ${fmt(d.amount)} donation from ${donorName(d)}?\n\nIt will no longer count toward totals.`)) return;
    try {
      await fundraisingService.updateDonation({ id: d.id, status: 'inactive' });
      showToast('Donation removed ✓');
      await loadDonations(networkId, { force: true });
    } catch (err) {
      showToast(err.message);
    }
  };

  const exportCSV = () => {
    const rows = [['Type', 'Parent / Description', 'Amount', 'Source / Tagged To', 'Date', 'Reason']];
    donations.forEach((d) =>
      rows.push([
        'Donation',
        donorName(d),
        d.amount.toFixed(2),
        donationSourceLabel(d.source),
        d.date,
        (d.reason || '') + (d.note ? ' — ' + d.note : ''),
      ])
    );
    expenses.forEach((e) =>
      rows.push(['Expense', e.desc, '-' + e.amount.toFixed(2), e.category, e.date, ''])
    );
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kiker-1st-grade-fundraising.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('CSV downloaded ✓');
  };

  const sortedDonations = [...donations].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const sortedExpenses = [...expenses].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="sec on">
      <SectionHead
        title="Fundraising"
        sub="Track donations and how the class fund gets spent — replaces the spreadsheet."
        actions={
          <button className="btn btn-ghost" onClick={exportCSV}>
            <IconExport />
            Export
          </button>
        }
      />

      <div className="stat-row">
        <StatCard label="Total Raised" value={fmt(raised)} />
        <StatCard variant="spent" label="Total Spent" value={fmt(spent)} />
        <StatCard variant="balance" label="Balance" value={fmt(balance)} />
      </div>

      <Card
        title="Donations"
        action={<button className="btn btn-primary btn-sm" onClick={() => openDonForm()}>+ Log Donation</button>}
      >
        <DonationForm
          open={donFormOpen}
          openCount={donOpenCount}
          onClose={closeDonForm}
          parents={classParents}
          editing={editingDonation}
          onSave={saveDonation}
          emailFor={confirmationEmailFor}
          gmailConnected={!!gmailAccount}
        />
        <div className="table-scroll">
        {/* table-stack: on a phone the row becomes name + amount, then the
            remaining cells run on as one sentence — the cell roles drive it */}
        <table className="table table-stack">
          <thead>
            <tr>
              <th>Parent</th>
              <th>Amount</th>
              <th>Source</th>
              <th>Reason</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {donationsLoading && (
              <tr>
                <td colSpan={6} className="empty">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Spinner variant="green" /> Loading donations…
                  </span>
                </td>
              </tr>
            )}
            {!donationsLoading && sortedDonations.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">No donations logged yet.</td>
              </tr>
            )}
            {!donationsLoading &&
              sortedDonations.map((d) => {
                const name = donorName(d);
                const member = memberById(d.userId);
                const av = avatarFor(name, []);
                return (
                  <tr key={d.id}>
                    <td className="c-main">
                      <div className="who">
                        <Avatar src={member?.picture || d.parentPicture} initials={initialsOf(name)} color={av.color} size={24} fontSize={9.5} />
                        {name}
                      </div>
                    </td>
                    <td className="c-amt">{fmt(d.amount)}</td>
                    <td className="c-meta">
                      <span className="src-chip">{donationSourceLabel(d.source) || '—'}</span>
                    </td>
                    <td className="c-meta">
                      {d.reason || '—'}
                      {d.note && <div className="cell-sub">{d.note}</div>}
                    </td>
                    <td className="c-meta">
                      {fmtDate(d.date)}
                      {loggedByName(d) && <div className="cell-sub">Logged by {loggedByName(d)}</div>}
                    </td>
                    <td className="c-act">
                      <div className="row-actions">
                        <button className="btn btn-ghost btn-sm" onClick={() => openDonForm(d)}>Edit</button>
                        <div className="ev-trash" title="Remove this donation" onClick={() => deactivateDonation(d)}>
                          <IconTrash />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        </div>
      </Card>

      <Card
        title="Expenses"
        action={<button className="btn btn-primary btn-sm" onClick={() => openExpForm()}>+ Log Expense</button>}
      >
        <ExpenseForm
          open={expFormOpen}
          openCount={expOpenCount}
          onClose={closeExpForm}
          editing={editingExpense}
          onSave={saveExpense}
        />
        <div className="table-scroll">
        <table className="table table-stack">
          <thead>
            <tr>
              <th>What for</th>
              <th>Tagged To</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Receipt</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {expensesLoading && (
              <tr>
                <td colSpan={6} className="empty">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Spinner variant="green" /> Loading expenses…
                  </span>
                </td>
              </tr>
            )}
            {!expensesLoading && sortedExpenses.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">No expenses logged yet.</td>
              </tr>
            )}
            {!expensesLoading &&
              sortedExpenses.map((e) => (
                <tr key={e.id}>
                  <td className="c-main">{e.desc}</td>
                  <td className="c-meta">{e.category || '—'}</td>
                  <td className="c-amt" style={{ color: 'var(--red)' }}>-{fmt(e.amount)}</td>
                  <td className="c-meta">
                    {fmtDate(e.date)}
                    {loggedByName(e) && <div className="cell-sub">Logged by {loggedByName(e)}</div>}
                  </td>
                  <td className="c-receipt">
                    {e.receipt ? (
                      <a href={e.receipt} target="_blank" rel="noreferrer">
                        <img className="receipt-thumb" src={e.receipt} alt="Receipt" />
                      </a>
                    ) : (
                      <div className="receipt-empty" title="No receipt attached">
                        <IconFile />
                      </div>
                    )}
                  </td>
                  <td className="c-act">
                    <div className="row-actions">
                      <button className="btn btn-ghost btn-sm" onClick={() => openExpForm(e)}>Edit</button>
                      <div className="ev-trash" title="Remove this expense" onClick={() => deactivateExpense(e)}>
                        <IconTrash />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        </div>
      </Card>

      {/* the two donuts summarise the ledgers above them, so they sit last */}
      <div className="dash-grid">
        <Card title="Donations by Reason">
          <DonutChart entries={toChartEntries(donationsByReason)} centerCaption="raised" />
        </Card>
        <Card title="Expenses by Event">
          <DonutChart entries={toChartEntries(expensesByTag)} centerCaption="spent" />
        </Card>
      </div>

      <ConfirmDialog
        open={!!noGmailAsk}
        title="Gmail isn't connected"
        tone="primary"
        confirmLabel="Save without email"
        cancelLabel="Cancel"
        onCancel={() => answerNoGmail(false)}
        onConfirm={() => answerNoGmail(true)}
      >
        <p style={{ fontSize: 13, color: 'var(--body)', lineHeight: 1.6, margin: '0 0 10px' }}>
          You can save this donation, but{' '}
          <b style={{ color: 'var(--ink)' }}>{noGmailAsk?.parentName || 'the parent'}</b> won't get a confirmation email
          — the email is sent from your Gmail, and it isn't connected.
        </p>
        <p style={{ fontSize: 13, color: 'var(--body)', lineHeight: 1.6, margin: 0 }}>
          To send it, cancel, connect Gmail at the top right, then save again.{' '}
          <button
            type="button"
            className="switch-link"
            onClick={() => {
              answerNoGmail(false);
              connectGmail();
            }}
          >
            Connect Gmail now
          </button>
        </p>
      </ConfirmDialog>
    </div>
  );
}
